import type { InsightFamily } from "@amzn/d16-g-client-deck-builder-typescript-client";
import type { AsyncStatus } from "@react-hookz/web";
import { useDebouncedEffect } from "@react-hookz/web";
import { useCallback, useEffect, useRef, useState } from "react";
import { useStore } from "zustand";

import { insightFamiliesFilters } from "../../config/filters/insightFamilies";
import { FeatureFlagsStore } from "../../store/featureFlags";
import { discoveryFilterStore } from "../../store/filters";
import type { InsightFamiliesFilterField } from "../../types/filters/insightFamilies";
import { IHubContentType } from "../../types/ihubContentType";
import { IHubContentProvider } from "../../types/providers";
import { isLoading, isSuccess } from "../../utils/api.utils";
import { getFilterOptions } from "../../utils/filters.utils";
import type { InsightFamiliesFilters } from "../../utils/insightFamilies.utils";
import {
    getAdvertiserId,
    getMarketplaceId,
} from "../../utils/permission.utils";
import { logInsightBrowseResults } from "../../utils/telemetry.utils";
import { getInsightIdFromSearchParams } from "../../utils/window.utils";

import {
    DEFAULT_GET_ALL_FAMILIES_PAGE_SIZE,
    useGetAllInsightFamiliesAsync,
} from "./useAllInsightFamiliesAction";
import { useGetInsightFamiliesFiltersAsync } from "./useInsightFamiliesFiltersAction";
import { useGetInsightFamilyAction } from "./useInsightFamilyAction";

export interface InsightFamiliesLoaderProps {
    isLoaderInView?: boolean;
}

export function useInsightFamiliesLoader({
    isLoaderInView,
}: InsightFamiliesLoaderProps): [AsyncStatus, InsightFamily[], number] {
    const [useUnifiedSearch] = useStore(FeatureFlagsStore, (state) => [
        state.unifiedSearch,
    ]);
    const [insights, setInsights] = useState<InsightFamily[]>([]);
    const [filters, setFilters] = useState<InsightFamiliesFilters>();
    const [insightCount, setInsightCount] = useState<number>(0);
    const pageNumber = useRef(1);

    const [
        setFilterOptions,
        setData,
        fieldSelections,
        contentSubType,
        searchQuery,
        setTypeOptions,
        resetTypeOptions,
        getContentType,
    ] = useStore(discoveryFilterStore, (state) => [
        state.setFilterOptions,
        state.setData,
        state.getInsightsFilters().INSIGHT_FAMILIES.fieldSelections,
        state.getContentSubType(),
        state.getInsightsFilters()[IHubContentProvider.INSIGHT_FAMILIES]
            .searchQuery,
        state.setTypeOptions,
        state.resetTypeOptions,
        state.getContentType,
    ]);

    const advertiserId = getAdvertiserId()!;
    const marketplaceId = getMarketplaceId()!;
    const previousFieldSelection = useRef(fieldSelections);

    const [getInsightsStateV2, getInsightsActionV2] =
        useGetAllInsightFamiliesAsync({
            advertiserId,
            marketplaceId,
        });

    const [_, getFiltersActionV2] = useGetInsightFamiliesFiltersAsync({
        advertiserId,
        marketplaceId,
    });

    const insightIdentifier = getInsightIdFromSearchParams();

    const enableSingleIdentifierView = insightIdentifier;

    const [getInsightState, getInsightAction] = useGetInsightFamilyAction({
        advertiserId,
        marketplaceId,
        familyInstanceId: insightIdentifier,
    });

    /**
     * Performs all of the logic needed to retrieve the insights from the the api
     */
    const fetchInsightsV2 = useCallback(
        async (isPagination = false) => {
            let curInsights = insights;
            if (!isPagination) {
                setInsights([]);
                curInsights = [];
                pageNumber.current = 1;
            }

            await getInsightsActionV2
                .execute(pageNumber.current)
                .then(({ data, insightsCount }) => {
                    // This is a temporary fix to address two known bugs until the istore team can address them in their API
                    // 1. The istore API will return 0 for insight count if we request a page with no results, so we should use the results from the first page
                    // 2. Their api filters insights out before returning, so insightsCount may be 35, but only 30 results are returned
                    if (pageNumber.current === 1) {
                        insightsCount < DEFAULT_GET_ALL_FAMILIES_PAGE_SIZE
                            ? setInsightCount(data.length)
                            : setInsightCount(insightsCount);
                    }

                    setData(IHubContentProvider.INSIGHT_FAMILIES, [
                        ...curInsights,
                        ...data,
                    ]);
                    setInsights([...curInsights, ...data]);
                    pageNumber.current = pageNumber.current + 1;
                });
        },
        [searchQuery, fieldSelections, pageNumber, insights],
    );

    /**
     * Performs all of the logic needed to retrieve the filters from the the api
     */
    const fetchFiltersV2 = useCallback(async () => {
        const filterResponse = await getFiltersActionV2.execute({});

        const familyOptions = await getFiltersActionV2.execute({
            ignoreFamily: true,
        });

        setTypeOptions(IHubContentProvider.INSIGHT_FAMILIES, [
            "All",
            ...familyOptions.families,
        ]);

        const newFilters: InsightFamiliesFilters = {
            ...filterResponse.filters,
        };

        // For any field that has a selected value, we will want to do a query where we are ignoring that selected value
        // This results in a list of all possible options that would INCREASE the number of results shown
        await Promise.all(
            insightFamiliesFilters
                .filter((filter) => fieldSelections[filter.field].length !== 0)
                .map(async (filter) => {
                    await getFiltersActionV2
                        .execute({
                            ignoreField:
                                filter.field as InsightFamiliesFilterField,
                        })
                        .then(({ filters }) => {
                            newFilters[filter.field] = filters[filter.field];
                        });
                }),
        );

        setFilters(newFilters);
    }, [searchQuery, fieldSelections]);

    /**
     * If we have an insight indentifier in the URL, then load just that singular insight
     */
    useEffect(() => {
        if (insightIdentifier) {
            void (async () => {
                const { insightFamily, filters } =
                    await getInsightAction.execute();
                const insights = [insightFamily!];
                setData(IHubContentProvider.INSIGHT_FAMILIES, insights);
                setInsights(insights);
                setFilters(filters);
                setInsightCount(insights.length);
            })();
        }
    }, [insightIdentifier]);

    /**
     * Update our rendered list of filters when:
     * 1. filters - There is a change in the filter values returned from the api
     */
    useEffect(() => {
        filters &&
            insightFamiliesFilters.forEach((filter) => {
                setFilterOptions(
                    filter.field,
                    getFilterOptions({
                        ...filters[filter.field],
                        ...fieldSelections[filter.field],
                    }).filter(
                        (val, i, arr) =>
                            i ===
                            arr.findIndex((val2) => val2.label === val.label),
                    ),
                );
            });

        previousFieldSelection.current = fieldSelections;
    }, [filters]);

    /**
     * Trigger a refresh of the data when the user scrolls to the bottom of the page, max 1 per second
     */
    useDebouncedEffect(
        () => {
            if (
                isLoaderInView &&
                !isLoading(getInsightsStateV2.status) &&
                pageNumber.current <=
                    Math.ceil(
                        insightCount / DEFAULT_GET_ALL_FAMILIES_PAGE_SIZE,
                    ) &&
                !enableSingleIdentifierView
            ) {
                void fetchInsightsV2(true);
            }
        },
        [
            isLoaderInView,
            insightCount,
            pageNumber,
            DEFAULT_GET_ALL_FAMILIES_PAGE_SIZE,
            enableSingleIdentifierView,
        ],
        500,
    );

    // When we leave / unload this component, reset our filter state
    useEffect(() => {
        return () => {
            getContentType() === IHubContentType.INSIGHTS &&
                resetTypeOptions(IHubContentProvider.INSIGHT_FAMILIES);
        };
    }, []);

    /**
     * Trigger a refresh of the insights after 500ms of inactivity when:
     * 1. searchQuery - The user has changed the term they're searching for
     * 2. fieldSelections - The user has changed the filters they want to apply
     * 3. contentSubType - The user selected a new insight type
     */
    useDebouncedEffect(
        () => {
            if (!enableSingleIdentifierView) {
                void fetchInsightsV2(false);
                void fetchFiltersV2();
            }
        },
        [
            searchQuery,
            fieldSelections,
            contentSubType,
            enableSingleIdentifierView,
        ],
        500,
    );

    /**
     * Log data each time we retrieve insights
     */
    useDebouncedEffect(
        () => {
            if (isSuccess(getInsightsStateV2.status)) {
                setData(IHubContentProvider.INSIGHT_FAMILIES, insights);
                logInsightBrowseResults(insights.length, useUnifiedSearch);
            }
        },
        [insights, getInsightsStateV2.status],
        500,
    );

    if (enableSingleIdentifierView) {
        return [getInsightState.status, insights, insightCount];
    } else {
        return [getInsightsStateV2.status, insights, insightCount];
    }
}
